import React, { useEffect, useImperativeHandle } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { computeText } from '@/locales/utils';
import { theme } from '@/core/theme';
import { WebhookConfig, WebhookHeader } from '../../../../pages/Config/Webhooks/types';
import { BrandWebhookConfigurationPanelProps, BrandWebhookConfigurationPanelViewModel } from './types';

export const useBrandWebhookConfigurationPanelVM = (
    { webhook, setValid, setDirty }: BrandWebhookConfigurationPanelProps,
    ref: React.Ref<unknown>,
): BrandWebhookConfigurationPanelViewModel => {
    const intl = useIntl();
    const fieldRequiredErrorText = computeText(intl, 'field.required');
    const invalidUrlErrorText = computeText(intl, 'field.invalidURL');
    const atLeastOneEventTypeErrorText = computeText(intl, 'field.atLeastOneEventType');
    const isEditWebhook = !!webhook?.webhookHmacSecret;

    const WebhookFormResolver = yupResolver(
        Yup.object().shape({
            webhookUrl: Yup.string().url(invalidUrlErrorText).required(fieldRequiredErrorText),
            additionalHeaders: Yup.array()
                .of(
                    Yup.object().shape({
                        name: Yup.string().required(fieldRequiredErrorText),
                        value: Yup.string().required(fieldRequiredErrorText),
                    }),
                )
                .transform((headers) =>
                    headers.filter(({ name, value }: WebhookHeader) => name !== '' || value !== ''),
                ),
            channels: Yup.array(Yup.number()),
            eventTypes: Yup.array(Yup.string()).min(1, atLeastOneEventTypeErrorText),
        }),
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid, isDirty },
    } = useForm<WebhookConfig>({
        mode: 'all',
        defaultValues: {
            ...webhook,
            webhookHmacSecret: undefined,
            additionalHeaders: [...(webhook?.additionalHeaders || []), { name: '', value: '' }],
            eventTypes: webhook?.eventTypes || [],
        },
        resolver: WebhookFormResolver,
    });

    const {
        fields: headers,
        append,
        remove,
    } = useFieldArray({
        control,
        name: 'additionalHeaders',
    });

    const toggleChannel = (value: number[], channelId: number, onChange: (channels: number[]) => void) => {
        const channels = [...value];
        const channelIndex = channels.indexOf(channelId);
        if (channelIndex > -1) channels.splice(channelIndex, 1);
        else channels.push(channelId);
        onChange(channels);
    };

    const copyToClipboard = (event: React.MouseEvent<HTMLSpanElement>, text: string) => {
        const button = event.currentTarget;
        navigator.clipboard.writeText(text).then(() => {
            button.style.color = theme.color.green200;
            setTimeout(() => {
                button.style.color = 'unset';
            }, 300);
        });
    };

    useImperativeHandle(
        ref,
        () => ({
            handleSubmit,
            reset,
        }),
        [handleSubmit, reset],
    );

    useEffect(() => {
        setValid(isValid);
    }, [setValid, isValid]);

    useEffect(() => {
        setDirty?.(isDirty);
    }, [setDirty, isDirty]);

    return {
        control,
        headers,
        isEditWebhook,
        webhook,
        toggleChannel,
        copyToClipboard,
        append,
        remove,
        Controller,
    };
};
